import { lazy, Suspense } from 'react'
import { useDispatch } from 'react-redux'
import { useRoutes, Navigate, Outlet } from 'react-router-dom'

import {
    MainLayout,
    ErrorLayout,
    AdminLayout as AdminMainLayout,
} from '@/components/Layout'
// eslint-disable-next-line no-restricted-imports
import { AdminDashboard } from '@/features/admin/routes/AdminDashboard'
// eslint-disable-next-line no-restricted-imports
import { SearchTimes } from '@/features/admin/routes/searchTimes'
import { AuthGuard, AdminGuard, AuthCallback, Logout } from '@/features/auth'
// eslint-disable-next-line no-restricted-imports
import { Contacts } from '@/features/contact/routes/Contacts'
import { clearRevisionCheck } from '@/stores'

const Evaluations = lazy(() =>
    import('@/features/high-performers/routes.feedback')
)
const Dashboard = lazy(() => import('@/features/dashboard/routes.dashboard'))
const Dashboard2 = lazy(() => import('@/features/dashboard/route.dashboard.v2'))
const NewDevelopment = lazy(() =>
    import('@/features/new-development/routes.newdevelopment')
)
const FacilitiesRoutes = lazy(() =>
    import('@/features/facilities/routes.facilities')
)
const ProposalsRoutes = lazy(() =>
    import('@/features/proposals/routes.proposals')
)
const OffersRoutes = lazy(() => import('@/features/offers/routes.offers'))
const SearchContainer = lazy(() =>
    import('@/features/search/route.search.container')
)
const SearchIndex = lazy(() => import('@/features/search/route.search.index'))
// const SearchMaps = lazy(() => import('@/features/search/route.search.maps'))
const SearchDemo = lazy(() => import('@/features/search/route.search.demo'))
const ProposalSupport = lazy(() =>
    import('@/features/job-proposals/route.support')
)

// 提案書改善で追加した画面
const JobSeekerLayout = lazy(() => import('@/features/jobseekers/route.layout'))
const ContactHistoryPage = lazy(() =>
    import('@/features/jobseekers/route.history')
)
const SearchEntryPage = lazy(() =>
    import('@/features/search/route.search.entry')
)
const CreateProposalPage = lazy(() =>
    import('@/features/job-proposals/route.document.create')
)
const EditProposalPage = lazy(() =>
    import('@/features/job-proposals/route.document.edit')
)

//新規開拓支援v2で追加した画面
// const InformationInputPage = lazy(() =>
//     import('@/features/information-input/route.information-input')
// )

const CallInfoInput = lazy(() =>
    import('@/features/job-create/route.CallInfoInput.create')
)
const JobInfoInput = lazy(() =>
    import('@/features/job-create/route.JobInfoInput.create')
)
const JobFeatureInput = lazy(() =>
    import('@/features/job-create/route.JobFeatureInput.create')
)
const CaCommentInput = lazy(() =>
    import('@/features/job-create/route.CaCommentInput.create')
)
const InputInfoConfirm = lazy(() =>
    import('@/features/job-create/route.InputInfoConfirm')
)

const SelectFacility = lazy(() =>
    import('@/features/job-create/route.SelectFacility')
)
const ReCallList = lazy(() =>
    import('@/features/new-development/routes.ReCallList')
)
const RemandList = lazy(() =>
    import('@/features/new-development/routes.RemandList')
)
const SaveList = lazy(() =>
    import('@/features/new-development/routes.SaveList')
)
const ProposalPreview = lazy(() =>
    import('@/features/new-development/routes.ProposalPreview')
)
const ProposalPagePreview = lazy(() =>
    import('@/features/new-development/routes.ProposalPagePreview')
)

//'@/features/new-development/routes.newdevelopment'
//いろいろ追加

const Loading = () => <p>Loading...</p>

const App = () => {
    const dispatch = useDispatch()
    // FIXME: クリアのタイミングを見直す
    dispatch(clearRevisionCheck())
    return (
        <AuthGuard>
            <Outlet />
        </AuthGuard>
    )
}

const UserLayout = () => {
    return (
        <MainLayout>
            <Suspense fallback={<Loading />}>
                <Outlet />
            </Suspense>
        </MainLayout>
    )
}

/**
 * 案件起点 (tryt-offer-driven) の iframe modal から `/proposals/:proposalV2Id/pages/:proposalPageId/preview`
 * を埋め込み表示するための **chrome 無し layout**。`UserLayout`
 * (= MainLayout = グローバルヘッダー / フッター付き) との違いは MainLayout を
 * 被せない点だけ。
 *
 * 経緯: MR3076 で `/preview` route を `UserLayout` の child に置いていたため、
 * recom 内 modal 経由 (Chakra UI Modal の overlay でヘッダーが隠れる) では
 * 問題化しなかったが、案件起点 iframe で開くと iframe 上部に MainLayout の
 * `<Header />` が実体として描画されてしまっていた。本 layout で `/preview`
 * route だけ MainLayout 外に出し、案件起点 iframe / 通常 SPA 内 modal の
 * どちらの経路でも chrome 無しで正しく描画されるようにする。
 *
 * - 認証は親 `App` で `<AuthGuard>` 済 (= sibling `UserLayout` と同じ条件、
 *   advisor 認証必須の semantics を維持)
 * - lazy import された route element 用に `<Suspense>` を保持
 * - `/preview` 以外の URL (例: `/proposals/:proposalV2Id/pages/:proposalPageId`
 *   = 通常 page open) は従来どおり `UserLayout` 配下に残す
 */
const EmbedLayout = () => {
    return (
        <Suspense fallback={<Loading />}>
            <Outlet />
        </Suspense>
    )
}

const AdminApp = () => {
    return (
        <AdminGuard>
            <Outlet />
        </AdminGuard>
    )
}

const AdminLayout = () => {
    return (
        <AdminMainLayout>
            <Suspense fallback={<Loading />}>
                <Outlet />
            </Suspense>
        </AdminMainLayout>
    )
}

export const AppRoutes = () => {
    const routes = [
        // see: https://reacttraining.com/blog/react-router-v6-pre/#object-based-routes
        {
            path: '/',
            element: <App />,
            children: [
                {
                    path: '/contacts',
                    element: <Contacts />,
                },
                {
                    path: '/evaluations',
                    element: <Evaluations />,
                },
                {
                    path: '/admin',
                    element: <AdminApp />,
                    children: [
                        {
                            path: 'search/view',
                            element: <SearchDemo />,
                        },
                        {
                            path: '/admin',
                            element: <AdminLayout />,
                            children: [
                                {
                                    path: 'dashboard',
                                    element: <AdminDashboard />,
                                },
                                {
                                    path: 'searchTimes',
                                    element: <SearchTimes />,
                                },

                                {
                                    path: '',
                                    element: <Navigate to="dashboard" />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: '/',
                    element: <UserLayout />,
                    children: [
                        {
                            path: '/job-seekers/:sfId',
                            element: <JobSeekerLayout />,
                            children: [
                                {
                                    index: true,
                                    element: <ContactHistoryPage />,
                                },
                                {
                                    path: 'proposals/create',
                                    element: <CreateProposalPage />,
                                },
                                {
                                    path: 'proposals/:proposalId',
                                    element: <EditProposalPage />,
                                },
                                {
                                    path: 'recommendations',
                                    element: <ProposalSupport />,
                                },
                                {
                                    path: 'entry',
                                    element: <SearchEntryPage />,
                                },
                            ],
                        },
                        {
                            path: '/facilities/*',
                            element: <FacilitiesRoutes />,
                        },
                        {
                            path: '/new-development',
                            children: [
                                {
                                    index: true,
                                    element: <NewDevelopment />,
                                },
                                {
                                    path: 'reCallList',
                                    element: <ReCallList />,
                                },
                                {
                                    path: 'remandList',
                                    element: <RemandList />,
                                },
                                {
                                    path: 'saveList',
                                    element: <SaveList />,
                                },
                                {
                                    path: 'proposal/:slug',
                                    element: <ProposalPreview />,
                                },
                                // {
                                //     //
                                //     path: 'job-create/select-facility',
                                //     element: <SelectFacility />,
                                // },
                                {
                                    // path: 'job-create/:code',

                                    path: 'job-create',
                                    children: [
                                        {
                                            path: 'select-facility',
                                            element: <SelectFacility />,
                                        }, // ここで施設選択
                                        {
                                            path: ':code',
                                            children: [
                                                {
                                                    path: 'call-info',
                                                    element: <CallInfoInput />,
                                                },
                                                {
                                                    path: 'job-info',
                                                    element: <JobInfoInput />,
                                                },
                                                {
                                                    path: 'job-feature',
                                                    element: (
                                                        <JobFeatureInput />
                                                    ),
                                                },
                                                {
                                                    path: 'ca-info',
                                                    element: <CaCommentInput />,
                                                },
                                                {
                                                    path: 'info-confirm',
                                                    element: (
                                                        <InputInfoConfirm />
                                                    ),
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            // `/proposals/:proposalV2Id/pages/:proposalPageId` を `/proposals/*`
                            // (= ProposalsRoutes) より前に置く。React Router v6 は
                            // 静的 + param 経路を `/*` より優先するが、明示的に sibling を
                            // 上に置くことで意図を明確にする。
                            //
                            // 注: `/preview` は **embed mode (= MainLayout 外)** で
                            // 描画するため、本 `UserLayout` 配下には置かず、後段の
                            // `EmbedLayout` 配下に分離している (= 案件起点 iframe で
                            // recom グローバルヘッダーが見えてしまう問題の修正)。
                            path: '/proposals/:proposalV2Id/pages/:proposalPageId',
                            element: <ProposalPagePreview />,
                        },
                        { path: '/proposals/*', element: <ProposalsRoutes /> },
                        { path: '/top', element: <Dashboard2 /> },
                        { path: '/dashboard', element: <Dashboard /> },
                        { path: '/offers/*', element: <OffersRoutes /> },
                        // {
                        //     path: '/information-input',
                        //     element: <InformationInputPage />,
                        // },
                        { path: '/searchTimes', element: <SearchTimes /> },
                        { path: '/search/demo', element: <SearchDemo /> },
                        {
                            path: '/search',
                            element: <SearchContainer />,
                            children: [
                                {
                                    index: true,
                                    element: <SearchIndex />,
                                },
                                // {
                                //     path: 'maps',
                                //     element: <SearchMaps />,
                                // },
                            ],
                        },
                        {
                            path: '/',
                            element: <Navigate to="/top" />,
                        },
                    ],
                },
                {
                    // 案件起点 iframe modal 用の **embed mode**。`UserLayout`
                    // (= MainLayout 配下) と sibling に置くことで、`App`
                    // (= AuthGuard) の child としては保たれ認証は維持される
                    // が、MainLayout の chrome (= グローバルヘッダー / フッター)
                    // が描画されない経路になる。詳細は `EmbedLayout` 定義参照。
                    path: '/proposals/:proposalV2Id/pages/:proposalPageId/preview',
                    element: <EmbedLayout />,
                    children: [
                        {
                            index: true,
                            element: <ProposalPagePreview preview />,
                        },
                    ],
                },
            ],
        },

        { path: '/error', element: <ErrorLayout /> },
        { path: '/oidccallback', element: <AuthCallback /> },
        { path: '/login', element: <Navigate to="/" /> },
        { path: '/logout', element: <Logout /> },
        {
            path: '/*',
            element: (
                <ErrorLayout
                    code={404}
                    message="ページが見つかりませんでした"
                />
            ),
        },
    ]

    const element = useRoutes(routes)

    return <>{element}</>
}
